/**
 * 评测配置 › 题库快照（`#eval-bank`，后台页面对齐批 3 B 路；用户 D-06：评测配置只读）。
 * 顶部选快照（默认最新）＋ 快照信息与派生来源链；下面四个页签：场景（每题「演什么」）／ 金标 ／ 诉求映射 ／
 * 模拟客户（所有题共用的「怎么演」：机器顾客提示词、模型、人设池、轮数上限与规定次数）／ 能力对齐闸（10-26：按当前能力清单逐题核的
 * 汇总、检查项、明细与出题规范；判分规则 EV-24，留出题明细只给题号，盲测集闸本身不打开）。
 * 盲测保护：`/items` 标了 `sealed` 的题只显示题号、题族、阶段、所属测试集，题面和金标位置写「盲测题，题面不公开」，
 * 也不为它请求 A-43。改动走 Loop 侧，再导出新题库快照。
 */
import { useEffect, useMemo, useState } from "react";

import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CodeBlock } from "@/components/ui/code-block";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { Pagination } from "@/components/ui/pagination";
import { LabeledSelect } from "@/components/ui/select";
import { Tabs } from "@/components/ui/tabs";
import {
	type PlanBankDetail,
	type PlanBankGate,
	type PlanBankItem,
	type PlanGateCheck,
	type PlanGateLevel,
	type PlanGateRow,
	type PlanGoldItem,
	type PlanRetiredSets,
	usePlanBankDetail,
	usePlanBankGate,
	usePlanBankItems,
	usePlanBankLineage,
	usePlanBankScenario,
	usePlanBankSim,
	usePlanBanks,
} from "@/lib/api/plan-eval-config";
import { planErrorMessage } from "@/lib/api/plan-lab";

import { useDemandLabel } from "../kit/demand-label";
import { FailState } from "../kit/fail-state";
import { Markdown } from "../kit/markdown";
import { PageJump } from "../kit/page-jump";
import { CellTags, type PlanColumn, PlanTable } from "../kit/plan-table";
import { BankPicker, BlockState, Field, SEALED_TEXT, shortTime } from "./shared";

type BankTabKey = "scenarios" | "gold" | "demand" | "sim" | "gate";
const TAB_ITEMS: { key: BankTabKey; label: string }[] = [
	{ key: "scenarios", label: "场景" },
	{ key: "gold", label: "金标" },
	{ key: "demand", label: "诉求映射" },
	{ key: "sim", label: "模拟客户" },
	{ key: "gate", label: "能力对齐闸" },
];
const ALL = "__all__";
const PAGE = 25;

const GOLD_ZH: Record<string, string> = {
	ready: "已就绪",
	pending_solvable: "待验可解",
	needs_fix: "待修",
};
const GOLD_TONE: Record<string, BadgeTone> = {
	ready: "success",
	pending_solvable: "warning",
	needs_fix: "danger",
};
const CHANGE_ZH: Record<string, string> = {
	scenarios_add: "新增题",
	scenarios_replace: "改题",
	gold_add: "新增金标",
	gold_replace: "改金标",
	prompts: "改提示词",
	test_sets: "改测试集",
	demand_type_map: "改诉求映射",
};

function GoldBadge({ row }: { row: PlanBankItem }) {
	if (row.sealed) return <Badge tone="neutral">盲测</Badge>;
	if (!row.has_gold) return <span className="text-text-muted">无金标</span>;
	const s = row.gold_status ?? "ready";
	return <Badge tone={GOLD_TONE[s] ?? "muted"}>{GOLD_ZH[s] ?? s}</Badge>;
}

/** 测试集名；退役集（判分规则 EV-26）标「已退役」——历史照常可看，不再发起批次 */
function setLabel(name: string, retired?: PlanRetiredSets): string {
	return retired?.[name] ? `${name}（已退役）` : name;
}

function uniq(xs: (string | null | undefined)[]): string[] {
	return [...new Set(xs.filter((x): x is string => !!x))].sort();
}

/* ── 页 ─────────────────────────────────────────────────────────── */

export function EvalBankTab() {
	const list = usePlanBanks();
	const [bankId, setBankId] = useState<string>("");
	const rows = list.data?.rows ?? [];
	useEffect(() => {
		const first = rows[0];
		if (!bankId && first) setBankId(first.bank_id);
	}, [bankId, rows]);
	const [tab, setTab] = useState<BankTabKey>("scenarios");

	if (list.isLoading || list.isError || !list.data)
		return <BlockState testId="plan-block-bank-meta" q={list} failTestId="plan-bank-fail" />;
	if (!rows.length)
		return (
			<Card data-testid="plan-block-bank-meta">
				<CardContent>
					<EmptyState
						title="还没有题库快照"
						desc="在 Loop 侧整理好题目后，用 scripts/plan_bank_export.py 导出一份。"
					/>
				</CardContent>
			</Card>
		);
	const id = bankId || rows[0]?.bank_id || "";
	return (
		<>
			<BankMeta rows={rows} bankId={id} onBank={setBankId} />
			<div className="space-y-3" data-testid="plan-block-bank-tabs">
				<Tabs items={TAB_ITEMS} active={tab} onChange={setTab} />
				<div data-testid={`plan-bank-pane-${tab}`}>
					{tab === "scenarios" ? <ScenariosPane bankId={id} /> : null}
					{tab === "gold" ? <GoldPane bankId={id} /> : null}
					{tab === "demand" ? <DemandPane bankId={id} /> : null}
					{tab === "sim" ? <SimPane bankId={id} /> : null}
					{tab === "gate" ? <GatePane bankId={id} /> : null}
				</div>
			</div>
		</>
	);
}

/* ── 快照信息 ＋ 派生来源链 ─────────────────────────────────────── */

function BankMeta({
	rows,
	bankId,
	onBank,
}: {
	rows: { bank_id: string; exported_at?: string | null }[];
	bankId: string;
	onBank: (v: string) => void;
}) {
	const d = usePlanBankDetail(bankId);
	const lineage = usePlanBankLineage(bankId);
	const [open, setOpen] = useState(false);
	const m = d.data?.manifest;
	const chain = lineage.data?.chain ?? [];
	const self = chain[0];
	return (
		<Card data-testid="plan-block-bank-meta">
			<CardContent className="space-y-3 py-4">
				<div className="flex flex-wrap items-center gap-3">
					<BankPicker rows={rows} value={bankId} onChange={onBank} />
					<span className="text-xs text-text-muted">只读：改动走 Loop 侧，再导出新题库快照</span>
				</div>
				{d.isLoading ? (
					<LoadingInline />
				) : d.isError || !m ? (
					<FailState
						reason={planErrorMessage(d.error)}
						onRetry={() => d.refetch()}
						retrying={d.isFetching}
						testId="plan-bank-detail-fail"
					/>
				) : (
					<div
						className="grid gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-4"
						data-testid="plan-bank-meta"
					>
						<Field label="快照号">
							<span className="font-mono">{bankId}</span>
						</Field>
						<Field label="导出时间">{shortTime(m.exported_at)}</Field>
						<Field label="题目 ／ 金标">
							{`${(m.scenario_count as number | undefined) ?? "—"} 题 ／ ${(m.gold_count as number | undefined) ?? "—"} 条（金标版本 ${m.gold_version ?? "—"}）`}
						</Field>
						<Field label="环境指纹">
							<span className="font-mono">
								{m.env_fingerprint ?? m.env?.env_fingerprint ?? "—"}
							</span>
							<span className="text-xs text-text-muted">
								{" "}
								· 工具后端 {m.env?.tool_backend?.default ?? "—"}
							</span>
						</Field>
						<div className="sm:col-span-2 xl:col-span-4">
							<Field label="派生来源">
								{lineage.isLoading ? (
									<LoadingInline />
								) : lineage.isError ? (
									<span className="text-text-muted">
										派生来源没加载出来（{planErrorMessage(lineage.error)}）
									</span>
								) : (
									<span className="flex flex-wrap items-baseline gap-x-2">
										<span>
											{self?.derived_from ? (
												<>
													派生自 <span className="font-mono">{self.derived_from}</span>：
												</>
											) : (
												"原始导出："
											)}
											<span className="text-text-secondary">{self?.note ?? "—"}</span>
										</span>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={() => setOpen(true)}
											data-testid="plan-bank-lineage-open"
										>
											看派生来源链（{chain.length} 版）
										</Button>
									</span>
								)}
							</Field>
						</div>
					</div>
				)}
			</CardContent>
			<Drawer
				open={open}
				onClose={() => setOpen(false)}
				width="min(760px,95vw)"
				testId="plan-bank-lineage-drawer"
				title="派生来源链"
				description="从这一份往上追，新 → 旧；每一步写了改了什么"
			>
				{chain.map((c, i) => (
					<DrawerSection
						key={c.bank_id}
						testId="plan-bank-lineage-step"
						title={
							<span className="flex flex-wrap items-center gap-2">
								<span className="font-mono">{c.bank_id}</span>
								{i === 0 ? <Badge tone="accent">当前</Badge> : null}
								<span className="text-xs font-normal text-text-muted">
									{shortTime(c.exported_at)}
									{c.scenario_count != null
										? ` · ${c.scenario_count} 题 ／ ${c.gold_count} 条金标`
										: ""}
								</span>
							</span>
						}
					>
						{c.missing ? (
							<div className="text-sm text-text-muted">这份快照不在仓库里</div>
						) : (
							<>
								<div className="flex flex-wrap gap-1">
									{Object.entries(c.changes ?? {}).map(([k, n]) => (
										<Badge key={k} tone="muted">
											{CHANGE_ZH[k] ?? k} {n}
										</Badge>
									))}
									{c.prompts_changed?.length ? (
										<Badge tone="info" className="font-mono">
											{c.prompts_changed.join("、")}
										</Badge>
									) : null}
								</div>
								<div className="whitespace-pre-wrap text-sm text-text-secondary">
									{c.note ?? "—"}
								</div>
								{c.derived_from ? (
									<div className="text-xs text-text-muted">
										派生自 <span className="font-mono">{c.derived_from}</span>
									</div>
								) : (
									<div className="text-xs text-text-muted">链的起点</div>
								)}
							</>
						)}
					</DrawerSection>
				))}
			</Drawer>
		</Card>
	);
}

/* ── 场景 ────────────────────────────────────────────────────────── */

function useItems(bankId: string) {
	return usePlanBankItems(bankId);
}

function PaneState({ q, testId }: { q: Parameters<typeof BlockState>[0]["q"]; testId: string }) {
	return (
		<Card>
			<CardContent className={q.isLoading ? "py-4" : undefined}>
				{q.isLoading ? (
					<LoadingInline />
				) : (
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId={testId}
					/>
				)}
			</CardContent>
		</Card>
	);
}

function ScenariosPane({ bankId }: { bankId: string }) {
	const demandZh = useDemandLabel();
	const q = useItems(bankId);
	const [set, setSet] = useState(ALL);
	const [fam, setFam] = useState(ALL);
	const [stage, setStage] = useState(ALL);
	const [page, setPage] = useState(0);
	const [open, setOpen] = useState<PlanBankItem | null>(null);
	const all = q.data?.rows ?? [];
	const rows = useMemo(
		() =>
			all.filter(
				(r) =>
					(set === ALL || r.test_sets.includes(set)) &&
					(fam === ALL || r.family === fam) &&
					(stage === ALL || r.stage === stage),
			),
		[all, set, fam, stage],
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: 换筛选或快照回第一页
	useEffect(() => setPage(0), [set, fam, stage, bankId]);
	if (q.isLoading || q.isError || !q.data) return <PaneState q={q} testId="plan-bank-items-fail" />;

	const opts = (xs: string[]) => [
		{ value: ALL, label: "全部" },
		...xs.map((x) => ({ value: x, label: x })),
	];
	const cols: PlanColumn<PlanBankItem>[] = [
		{
			key: "id",
			title: "题号",
			className: "w-64",
			render: (r) => <span className="font-mono">{r.scenario_id}</span>,
		},
		{ key: "family", title: "题族", className: "w-20", render: (r) => r.family },
		{ key: "stage", title: "阶段", className: "w-16", render: (r) => r.stage || "—" },
		{
			key: "intent",
			title: "意图",
			className: "w-28",
			render: (r) => (r.sealed ? <span className="text-text-muted">—</span> : demandZh(r.intent)),
		},
		{
			key: "difficulty",
			title: "难度",
			className: "max-w-[140px]",
			render: (r) =>
				r.sealed ? (
					<span className="text-text-muted">—</span>
				) : (
					<span className="block truncate" title={r.difficulty ?? ""}>
						{r.difficulty || "—"}
					</span>
				),
		},
		{
			key: "sets",
			title: "所属测试集",
			render: (r) => (
				<CellTags items={r.test_sets.map((x) => setLabel(x, q.data?.retired_sets))} mono />
			),
		},
		{ key: "gold", title: "金标", className: "w-24", render: (r) => <GoldBadge row={r} /> },
	];
	const pageCount = Math.ceil(rows.length / PAGE);
	return (
		<Card data-testid="plan-bank-scenarios">
			<CardContent className="space-y-3 py-4">
				<div className="flex flex-wrap items-center gap-3">
					<LabeledSelect
						label="测试集"
						value={set}
						onChange={setSet}
						options={opts(uniq(all.flatMap((r) => r.test_sets))).map((o) => ({
							...o,
							label: setLabel(o.label, q.data?.retired_sets),
						}))}
					/>
					<LabeledSelect
						label="题族"
						value={fam}
						onChange={setFam}
						options={opts(uniq(all.map((r) => r.family)))}
					/>
					<LabeledSelect
						label="阶段"
						value={stage}
						onChange={setStage}
						options={opts(uniq(all.map((r) => r.stage)))}
					/>
					{q.data.sealed_count ? (
						<span className="text-xs text-text-muted">
							其中 {q.data.sealed_count} 道盲测 ／ 留出题只显示题号、题族、阶段与所属测试集
						</span>
					) : null}
					{Object.entries(q.data.retired_sets ?? {}).map(([name, r]) => (
						<span
							key={name}
							className="text-xs text-text-muted"
							data-testid="plan-bank-retired-note"
						>
							{name} 已于 {r.retired_at ?? "—"} 退役（{r.reason ?? "—"}），改用{" "}
							{r.replaced_by ?? "—"}；历史批次与报告照常可看，不再发起批次
						</span>
					))}
				</div>
				{rows.length ? (
					<>
						<PlanTable
							columns={cols}
							rows={rows.slice(page * PAGE, page * PAGE + PAGE)}
							rowKey={(r) => r.scenario_id}
							onRowClick={setOpen}
							density="compact"
							rowTestId="plan-bank-scenario-row"
						/>
						<div className="flex items-center justify-between gap-3">
							<span className="text-xs text-text-muted">共 {rows.length} 条</span>
							<Pagination page={page} pageCount={pageCount} onChange={setPage} />
						</div>
					</>
				) : (
					<EmptyState title="没有符合条件的题" desc="换个测试集、题族或阶段看看。" />
				)}
			</CardContent>
			<ScenarioDrawer bankId={bankId} row={open} onClose={() => setOpen(null)} />
		</Card>
	);
}

function TextBlock({ text }: { text?: string | null }) {
	return <div className="whitespace-pre-wrap text-sm text-text-primary">{text || "—"}</div>;
}

/** 诉求编号：有编号显示编号 ＋ 中文名；没有（历史快照）显示按诉求映射归一的结果 */
function DemandCode({ item }: { item: PlanGoldItem }) {
	const demandZh = useDemandLabel();
	if (item.demand_code)
		return (
			<span className="text-xs text-text-muted" data-testid="plan-bank-demand-code">
				<span className="font-mono">{item.demand_code}</span> {demandZh(item.demand_norm)}
				{item.mention_codes?.length ? `（提及 ${item.mention_codes.join("、")}）` : null}
			</span>
		);
	return item.demand_norm ? (
		<span className="text-xs text-text-muted">归一为 {demandZh(item.demand_norm)}（未标编号）</span>
	) : null;
}

function GoldItems({ items }: { items: PlanGoldItem[] }) {
	return (
		<div className="space-y-3">
			{items.map((it) => (
				<div
					key={it.item_id}
					className="space-y-1.5 rounded-md border border-surface-border p-3"
					data-testid="plan-bank-gold-item"
				>
					<div className="flex flex-wrap items-center gap-2 text-sm">
						<Badge tone="neutral" className="font-mono">
							{it.item_id}
						</Badge>
						<span className="font-medium">{it.demand || "—"}</span>
						<DemandCode item={it} />
						<span className="text-text-muted">→</span>
						<Badge tone="info">{it.outcome || "—"}</Badge>
						{it.alt_outcomes?.length ? (
							<span className="text-xs text-text-muted">也可 {it.alt_outcomes.join("、")}</span>
						) : null}
						{it.flags?.map((f) => (
							<Badge key={f} tone="muted">
								{f}
							</Badge>
						))}
					</div>
					{it.object ? <div className="text-xs text-text-secondary">对象：{it.object}</div> : null}
					{it.alt_premise ? (
						<div className="text-xs text-text-secondary" data-testid="plan-bank-alt-premise">
							替代结局前提：{it.alt_premise}
						</div>
					) : null}
					{it.tools?.length ? (
						<div className="text-xs text-text-secondary">
							可接受工具：<span className="font-mono">{it.tools.join("、")}</span>
						</div>
					) : null}
					{it.requirements?.length ? (
						<div className="text-xs text-text-secondary">
							应答要求：{it.requirements.join("；")}
						</div>
					) : null}
					{it.forbid?.length ? (
						<div className="text-xs text-text-secondary">
							不该调：<span className="font-mono">{it.forbid.join("、")}</span>
						</div>
					) : null}
				</div>
			))}
		</div>
	);
}

function ScenarioDrawer({
	bankId,
	row,
	onClose,
}: { bankId: string; row: PlanBankItem | null; onClose: () => void }) {
	const demandZh = useDemandLabel();
	// 盲测题不请求 A-43（题面与金标不进浏览器）
	const sid = row && !row.sealed ? row.scenario_id : null;
	const q = usePlanBankScenario(bankId, sid);
	const s = q.data?.scenario;
	return (
		<Drawer
			open={!!row}
			onClose={onClose}
			width="min(820px,95vw)"
			testId="plan-bank-scenario-drawer"
			title={<span className="font-mono">{row?.scenario_id}</span>}
			description={
				row
					? [row.family, row.stage, row.sealed ? null : row.desc].filter(Boolean).join(" · ")
					: undefined
			}
		>
			{row ? (
				<>
					<DrawerSection title="基本信息">
						<div className="grid gap-3 sm:grid-cols-3">
							<Field label="题族">{row.family}</Field>
							<Field label="阶段">{row.stage || "—"}</Field>
							<Field label="所属测试集">{row.test_sets.join("、") || "—"}</Field>
							{row.sealed ? null : (
								<>
									<Field label="意图">
										{demandZh(row.intent)}
										<span className="ml-2 text-xs text-text-muted">
											说明见 <PageJump to="tags" />
										</span>
									</Field>
									<Field label="难度">{row.difficulty || "—"}</Field>
									<Field label="规定次数">{row.required_trials ?? "—"}</Field>
								</>
							)}
						</div>
					</DrawerSection>
					{row.sealed ? (
						<DrawerSection title="题面与金标" testId="plan-bank-sealed">
							<div className="text-sm text-text-muted">{SEALED_TEXT}</div>
							<div className="text-xs text-text-muted">
								盲测 ／ 留出题只用来验证，不作开发调参，后台不显示题面、判分契约和金标。
							</div>
						</DrawerSection>
					) : q.isLoading ? (
						<DrawerSection>
							<LoadingInline />
						</DrawerSection>
					) : q.isError || !s ? (
						<DrawerSection>
							<FailState
								reason={planErrorMessage(q.error)}
								onRetry={() => q.refetch()}
								retrying={q.isFetching}
								testId="plan-bank-scenario-fail"
							/>
						</DrawerSection>
					) : (
						<>
							<DrawerSection title="题面（演什么）" testId="plan-bank-scenario-body">
								<Field label="来意">
									<TextBlock text={s.reason_for_call} />
								</Field>
								<Field label="已知信息">
									<TextBlock text={s.known_info} />
								</Field>
								<Field label="答不上来的">
									<TextBlock text={s.unknown_info} />
								</Field>
								<Field label="行为要求">
									<TextBlock text={s.task_instructions} />
								</Field>
								<div className="grid gap-3 sm:grid-cols-3">
									<Field label="人设">{s.persona || "按人设池抽"}</Field>
									<Field label="顾客">
										<span className="font-mono">{s.customer_id || "—"}</span>
									</Field>
									<Field label="订单 ／ 商品">
										<span className="font-mono">
											{[...(s.data_refs?.orders ?? []), ...(s.data_refs?.products ?? [])].join(
												"、",
											) || "—"}
										</span>
									</Field>
								</div>
							</DrawerSection>
							<DrawerSection title="判分契约">
								<Field label="应调工具">
									<span className="font-mono">{s.eval?.expect_skills?.join("、") || "—"}</span>
								</Field>
								<Field label="禁调工具">
									<span className="font-mono">{s.eval?.forbid_skills?.join("、") || "—"}</span>
								</Field>
								<Field label="必须告知">{s.eval?.communicate_info?.join("、") || "—"}</Field>
								<Field label="关键断言">
									{s.eval?.nl_assertions?.length ? (
										<ol className="list-decimal space-y-1 pl-5">
											{s.eval.nl_assertions.map((a) => (
												<li key={a}>{a}</li>
											))}
										</ol>
									) : (
										"—"
									)}
								</Field>
							</DrawerSection>
							<DrawerSection title="金标事项">
								{row.gold_items?.length ? (
									<GoldItems items={row.gold_items} />
								) : (
									<div className="text-sm text-text-muted">本题没有金标</div>
								)}
							</DrawerSection>
						</>
					)}
				</>
			) : null}
		</Drawer>
	);
}

/* ── 金标 ────────────────────────────────────────────────────────── */

type GoldRow = { key: string; row: PlanBankItem; item: PlanGoldItem | null; n: number };

function GoldPane({ bankId }: { bankId: string }) {
	const demandZh = useDemandLabel();
	const q = useItems(bankId);
	const [set, setSet] = useState(ALL);
	const [outcome, setOutcome] = useState(ALL);
	const [page, setPage] = useState(0);
	const [open, setOpen] = useState<PlanBankItem | null>(null);
	const all = q.data?.rows ?? [];
	const flat = useMemo<GoldRow[]>(
		() =>
			all.flatMap((r) =>
				r.sealed
					? ([{ key: r.scenario_id, row: r, item: null, n: 0 }] as GoldRow[])
					: (r.gold_items ?? []).map(
							(it, i): GoldRow => ({
								key: `${r.scenario_id}:${it.item_id}:${i}`,
								row: r,
								item: it,
								n: r.gold_items?.length ?? 0,
							}),
						),
			),
		[all],
	);
	const rows = useMemo(
		() =>
			flat.filter(
				(g) =>
					(set === ALL || g.row.test_sets.includes(set)) &&
					(outcome === ALL || g.item?.outcome === outcome),
			),
		[flat, set, outcome],
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: 换筛选或快照回第一页
	useEffect(() => setPage(0), [set, outcome, bankId]);
	if (q.isLoading || q.isError || !q.data) return <PaneState q={q} testId="plan-bank-gold-fail" />;

	const opts = (xs: string[]) => [
		{ value: ALL, label: "全部" },
		...xs.map((x) => ({ value: x, label: x })),
	];
	const muted = <span className="text-text-muted">{SEALED_TEXT}</span>;
	const cols: PlanColumn<GoldRow>[] = [
		{
			key: "id",
			title: "题号",
			className: "w-64",
			render: (g) => <span className="font-mono">{g.row.scenario_id}</span>,
		},
		{
			key: "item",
			title: "事项",
			className: "w-14",
			render: (g) => (g.item ? <span className="font-mono">{g.item.item_id}</span> : "—"),
		},
		{
			key: "demand",
			title: "诉求类型",
			className: "max-w-[200px]",
			render: (g) =>
				g.item ? (
					<span
						className="block truncate"
						title={`${g.item.demand ?? ""} → ${demandZh(g.item.demand_norm)}`}
					>
						{g.item.demand || "—"}
					</span>
				) : (
					muted
				),
		},
		{
			key: "code",
			title: "诉求编号",
			className: "max-w-[240px]",
			render: (g) =>
				g.item ? (
					<span className="block truncate" title={g.item.demand_code ?? "未标编号，按诉求映射归一"}>
						{g.item.demand_code ? (
							<span className="font-mono text-xs">{g.item.demand_code}</span>
						) : (
							<span className="text-text-muted">未标编号</span>
						)}
					</span>
				) : null,
		},
		{
			key: "outcome",
			title: "期望结局",
			className: "w-36",
			render: (g) => (g.item ? <Badge tone="info">{g.item.outcome || "—"}</Badge> : null),
		},
		{
			key: "req",
			title: "关键断言（应答要求）",
			className: "max-w-[360px]",
			render: (g) =>
				g.item ? (
					<span
						className="block truncate text-text-secondary"
						title={(g.item.requirements ?? []).join("；")}
					>
						{(g.item.requirements ?? []).join("；") || "—"}
					</span>
				) : null,
		},
		{
			key: "sets",
			title: "所属测试集",
			render: (g) => (
				<CellTags items={g.row.test_sets.map((x) => setLabel(x, q.data?.retired_sets))} mono />
			),
		},
	];
	const pageCount = Math.ceil(rows.length / PAGE);
	const nQ = new Set(rows.map((g) => g.row.scenario_id)).size;
	return (
		<Card data-testid="plan-bank-gold">
			<CardContent className="space-y-3 py-4">
				<div className="flex flex-wrap items-center gap-3">
					<LabeledSelect
						label="测试集"
						value={set}
						onChange={setSet}
						options={opts(uniq(all.flatMap((r) => r.test_sets))).map((o) => ({
							...o,
							label: setLabel(o.label, q.data?.retired_sets),
						}))}
					/>
					<LabeledSelect
						label="期望结局"
						value={outcome}
						onChange={setOutcome}
						options={opts(uniq(flat.map((g) => g.item?.outcome)))}
					/>
				</div>
				{rows.length ? (
					<>
						<PlanTable
							columns={cols}
							rows={rows.slice(page * PAGE, page * PAGE + PAGE)}
							rowKey={(g) => g.key}
							onRowClick={(g) => setOpen(g.row)}
							density="compact"
							rowTestId="plan-bank-gold-row"
						/>
						<div className="flex items-center justify-between gap-3">
							<span className="text-xs text-text-muted">
								共 {nQ} 题 {rows.filter((g) => g.item).length} 件事项
							</span>
							<Pagination page={page} pageCount={pageCount} onChange={setPage} />
						</div>
					</>
				) : (
					<EmptyState
						title="没有符合条件的金标"
						desc="换个测试集或期望结局看看；没有金标的题不在这里列。"
					/>
				)}
			</CardContent>
			<ScenarioDrawer bankId={bankId} row={open} onClose={() => setOpen(null)} />
		</Card>
	);
}

/* ── 诉求映射 ────────────────────────────────────────────────────── */

type MapRow = { target: string; keys: string[] };

function groupMap(m: Record<string, string> | undefined): MapRow[] {
	const acc: Record<string, string[]> = {};
	for (const [k, v] of Object.entries(m ?? {})) {
		if (!acc[v]) acc[v] = [];
		acc[v].push(k);
	}
	return Object.entries(acc)
		.map(([target, keys]) => ({ target, keys }))
		.sort((a, b) => a.target.localeCompare(b.target));
}

function DemandPane({ bankId }: { bankId: string }) {
	const demandZh = useDemandLabel();
	const q = usePlanBankDetail(bankId);
	if (q.isLoading || q.isError || !q.data)
		return <PaneState q={q} testId="plan-bank-demand-fail" />;
	const map = (q.data as PlanBankDetail).manifest.demand_type_map ?? {};
	const byName = groupMap(map.by_name);
	const byTool = Object.entries(map.by_tool ?? {}).map(([tool, target]) => ({ tool, target }));
	const targetCell = (t: string) => (
		<span>
			<span className="font-mono">{t}</span>
			{demandZh(t) !== t ? <span className="text-text-muted"> {demandZh(t)}</span> : null}
		</span>
	);
	const coded = (q.data as PlanBankDetail).manifest.gold_demand_code;
	return (
		<div className="space-y-3">
			<div className="text-xs text-text-muted" data-testid="plan-bank-demand-note">
				{coded
					? "本快照金标已带诉求编号（意图编号，能力外为 eval.unsupported），比对事项直接用编号；下面的归一表只对没带编号的金标生效，留作历史快照兼容。"
					: "本快照金标没带诉求编号，比对事项时按下面的归一表把金标写法归一。"}
			</div>
			{coded?.rulings && Object.keys(coded.rulings).length ? (
				<div className="text-xs text-text-secondary" data-testid="plan-bank-demand-rulings">
					用户裁定（换写编号时优先于归一表）：
					{Object.entries(coded.rulings).map(([raw, code], i) => (
						<span key={raw}>
							{i ? "；" : ""}
							{raw} → <span className="font-mono">{code}</span>
						</span>
					))}
				</div>
			) : null}
			<div className="grid gap-4 xl:grid-cols-2" data-testid="plan-bank-demand">
				<Card>
					<CardContent className="space-y-3 py-4">
						<div className="space-y-0.5">
							<div className="text-sm font-semibold text-text-primary">按名称（by_name）</div>
							<div className="text-xs text-text-muted">
								金标里的诉求类型写法 → 机器取值（没带诉求编号的金标用）
							</div>
						</div>
						<PlanTable
							columns={[
								{
									key: "t",
									title: "机器取值",
									className: "w-48",
									render: (r: MapRow) => targetCell(r.target),
								},
								{
									key: "k",
									title: "金标写法",
									render: (r: MapRow) => <CellTags items={r.keys} max={4} />,
								},
							]}
							rows={byName}
							rowKey={(r) => r.target}
							density="compact"
							rowTestId="plan-bank-demand-name-row"
						/>
						<div className="text-xs text-text-muted">
							共 {byName.length} 个取值、{Object.keys(map.by_name ?? {}).length} 种写法
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="space-y-3 py-4">
						<div className="space-y-0.5">
							<div className="text-sm font-semibold text-text-primary">按工具（by_tool）</div>
							<div className="text-xs text-text-muted">
								写法查不到时，按这件事项可接受的工具兜底（查询告知类）
							</div>
						</div>
						<PlanTable
							columns={[
								{
									key: "tool",
									title: "工具",
									render: (r: { tool: string; target: string }) => (
										<span className="font-mono">{r.tool}</span>
									),
								},
								{
									key: "t",
									title: "机器取值",
									render: (r: { tool: string; target: string }) => targetCell(r.target),
								},
							]}
							rows={byTool}
							rowKey={(r) => r.tool}
							density="compact"
							rowTestId="plan-bank-demand-tool-row"
						/>
						<div className="text-xs text-text-muted">共 {byTool.length} 条</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

/* ── 模拟客户 ────────────────────────────────────────────────────── */

function SimPane({ bankId }: { bankId: string }) {
	const q = usePlanBankSim(bankId);
	if (q.isLoading || q.isError || !q.data) return <PaneState q={q} testId="plan-bank-sim-fail" />;
	const d = q.data;
	const turnRows = [
		{ family: "默认（其余题族）", turns: d.max_turns.default },
		...Object.entries(d.max_turns.by_family).map(([family, turns]) => ({ family, turns })),
	];
	return (
		<div className="space-y-4" data-testid="plan-bank-sim">
			<Card>
				<CardContent className="space-y-3 py-4">
					<div className="text-xs text-text-muted">
						所有题共用的「怎么演」：每道题的来意、已知信息、行为要求填进下面的提示词，由机器顾客按抽到的人设去演。
					</div>
					<div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-4">
						<Field label="机器顾客模型">
							<span className="font-mono">{d.model || "—"}</span>
						</Field>
						<Field label="提示词指纹">
							<span className="font-mono">
								{d.fingerprint ? d.fingerprint.replace(/^sha256:/, "").slice(0, 12) : "—"}
							</span>
						</Field>
						<Field label="对话轮数上限">
							{turnRows.map((t) => `${t.family} ${t.turns} 轮`).join("；")}
							<span className="text-xs text-text-muted">（发起批次时可统一改）</span>
						</Field>
						<Field label="规定次数（pass^k）">
							{`多诉求题 ${d.trials.multi} 次，其余 ${d.trials.single} 次`}
						</Field>
						{d.role_marker_resample != null ? (
							<Field label="串话重采">
								{`最多重采 ${d.role_marker_resample} 次`}
								<span className="text-xs text-text-muted">
									（客户的话里混进 user ／
									assistant、「客服：」之类角色标记时；仍失败截断并留痕，EV-22）
								</span>
							</Field>
						) : null}
					</div>
				</CardContent>
			</Card>
			<Card>
				<CardContent className="space-y-3 py-4">
					<div className="text-sm font-semibold text-text-primary">人设池</div>
					<PlanTable
						columns={[
							{
								key: "n",
								title: "人设",
								className: "w-32",
								render: (p: PlanBankSimPersona) => <span className="font-mono">{p.name}</span>,
							},
							{
								key: "w",
								title: "抽中概率",
								className: "w-24",
								align: "right",
								render: (p: PlanBankSimPersona) =>
									p.weight != null ? `${Math.round(p.weight * 100)}%` : "—",
							},
							{ key: "d", title: "说明", render: (p: PlanBankSimPersona) => p.desc || "—" },
						]}
						rows={d.personas}
						rowKey={(p) => p.name}
						density="compact"
						rowTestId="plan-bank-persona-row"
					/>
					<div className="text-xs text-text-muted">
						共 {d.personas.length} 个；题目指定了人设的按题目，没指定的按概率抽
					</div>
				</CardContent>
			</Card>
			<Card>
				<CardContent className="space-y-3 py-4">
					<div className="text-sm font-semibold text-text-primary">机器顾客提示词</div>
					{d.prompt_keys.map((k) => (
						<div key={k} className="space-y-1.5">
							<div className="font-mono text-xs text-text-muted">{k}</div>
							{d.prompts[k] ? (
								<CodeBlock code={d.prompts[k] ?? ""} maxHeight={480} />
							) : (
								<div className="text-sm text-text-muted">这份快照里没有</div>
							)}
						</div>
					))}
				</CardContent>
			</Card>
		</div>
	);
}

/* ── 能力对齐闸 ──────────────────────────────────────────────────── */

const LEVEL_ZH: Record<PlanGateLevel, string> = {
	block: "阻断",
	warn: "人工看",
	info: "提示",
};
const LEVEL_TONE: Record<PlanGateLevel, BadgeTone> = {
	block: "danger",
	warn: "warning",
	info: "muted",
};

function GateCount({ n, level }: { n: number; level: PlanGateLevel }) {
	return n ? (
		<Badge tone={LEVEL_TONE[level]}>{n}</Badge>
	) : (
		<span className="text-text-muted">0</span>
	);
}

function GatePane({ bankId }: { bankId: string }) {
	const q = usePlanBankGate(bankId);
	const [level, setLevel] = useState(ALL);
	const [check, setCheck] = useState(ALL);
	const [page, setPage] = useState(0);
	const [spec, setSpec] = useState(false);
	const all = q.data?.rows ?? [];
	const rows = useMemo(
		() =>
			all.filter(
				(r) => (level === ALL || r.level === level) && (check === ALL || r.check === check),
			),
		[all, level, check],
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: 换筛选或快照回第一页
	useEffect(() => setPage(0), [level, check, bankId]);
	if (q.isLoading || q.isError || !q.data) return <PaneState q={q} testId="plan-bank-gate-fail" />;
	const d = q.data;
	const countOf = (id: string, lv: PlanGateLevel) => d.by_check[`${id}·${lv}`] ?? 0;
	const checkName = Object.fromEntries(d.checks.map((c) => [c.id, c.name]));
	const cols: PlanColumn<PlanGateRow>[] = [
		{
			key: "id",
			title: "题号",
			className: "w-64",
			render: (r) => <span className="font-mono">{r.scenario_id}</span>,
		},
		{ key: "set", title: "所属集", className: "w-28", render: (r) => r.set },
		{
			key: "item",
			title: "事项",
			className: "w-14",
			render: (r) => <span className="font-mono">{r.item || "—"}</span>,
		},
		{
			key: "check",
			title: "检查项",
			className: "w-36",
			render: (r) => (
				<span>
					<span className="font-mono">{r.check}</span> {checkName[r.check] ?? ""}
				</span>
			),
		},
		{
			key: "level",
			title: "级别",
			className: "w-20",
			render: (r) => <Badge tone={LEVEL_TONE[r.level]}>{LEVEL_ZH[r.level]}</Badge>,
		},
		{
			key: "msg",
			title: "说明",
			render: (r) =>
				r.sealed ? (
					<span className="text-text-muted">留出题，只给题号</span>
				) : (
					<span className="block truncate text-text-secondary" title={r.msg ?? ""}>
						{r.msg || "—"}
					</span>
				),
		},
	];
	const pageCount = Math.ceil(rows.length / PAGE);
	return (
		<div className="space-y-4" data-testid="plan-bank-gate">
			<Card>
				<CardContent className="space-y-3 py-4">
					<div className="flex flex-wrap items-center gap-3">
						<span className="text-sm text-text-primary" data-testid="plan-bank-gate-summary">
							共核 {d.scenarios} 题：阻断 {d.block} 条、人工看 {d.warn} 条、提示 {d.info} 条
						</span>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="ml-auto"
							onClick={() => setSpec(true)}
							data-testid="plan-bank-gate-spec-open"
						>
							看出题规范
						</Button>
					</div>
					<div className="text-xs text-text-muted">
						按当前代码的能力清单（画像 <span className="font-mono">{d.profile}</span>、罐头今天{" "}
						{d.as_of}、清单{" "}
						<span className="font-mono">
							{d.capability_digest.replace(/^sha256:/, "").slice(0, 12)}
						</span>
						）逐题核期望是否在能力内、金标是否自洽、数据是否对得上；有阻断的题不能进判定集，CI
						对最新快照照此拦。
						{d.skipped_sets.length ? `盲测集（${d.skipped_sets.join("、")}）不打开；` : ""}
						{d.retired_sets?.length
							? `已退役集（${d.retired_sets.map((x) => x.name).join("、")}）不查；`
							: ""}
						留出题只给题号。
					</div>
					<PlanTable
						columns={[
							{
								key: "n",
								title: "测试集",
								render: (s: PlanBankGate["by_set"][number]) => (
									<span className="font-mono">{s.name}</span>
								),
							},
							{
								key: "c",
								title: "题数",
								className: "w-20",
								align: "right",
								render: (s: PlanBankGate["by_set"][number]) => s.scenarios,
							},
							...(["block", "warn", "info"] as const).map((lv) => ({
								key: lv,
								title: LEVEL_ZH[lv],
								className: "w-20",
								align: "right" as const,
								render: (s: PlanBankGate["by_set"][number]) => <GateCount n={s[lv]} level={lv} />,
							})),
						]}
						rows={d.by_set}
						rowKey={(s) => s.name}
						density="compact"
						rowTestId="plan-bank-gate-set-row"
					/>
				</CardContent>
			</Card>
			<Card>
				<CardContent className="space-y-3 py-4">
					<div className="text-sm font-semibold text-text-primary">检查项</div>
					<PlanTable
						columns={[
							{
								key: "id",
								title: "编号",
								className: "w-16",
								render: (c: PlanGateCheck) => <span className="font-mono">{c.id}</span>,
							},
							{
								key: "name",
								title: "名称",
								className: "w-32",
								render: (c: PlanGateCheck) => c.name,
							},
							{
								key: "lv",
								title: "级别",
								className: "w-36",
								render: (c: PlanGateCheck) =>
									c.level.replace(/block/g, "阻断").replace(/warn/g, "人工看"),
							},
							{
								key: "how",
								title: "怎么核",
								render: (c: PlanGateCheck) => (
									<span className="block truncate text-text-secondary" title={c.how}>
										{c.how}
									</span>
								),
							},
							{
								key: "hit",
								title: "本快照",
								className: "w-36",
								render: (c: PlanGateCheck) => {
									const hits = (["block", "warn", "info"] as const)
										.map((lv) => [lv, countOf(c.id, lv)] as const)
										.filter(([, n]) => n);
									return hits.length ? (
										<span className="flex flex-wrap gap-1">
											{hits.map(([lv, n]) => (
												<Badge key={lv} tone={LEVEL_TONE[lv]}>
													{LEVEL_ZH[lv]} {n}
												</Badge>
											))}
										</span>
									) : (
										<span className="text-text-muted">没报</span>
									);
								},
							},
						]}
						rows={d.checks}
						rowKey={(c) => c.id}
						density="compact"
						rowTestId="plan-bank-gate-check-row"
					/>
					{d.c16.length ? (
						<div className="text-xs text-text-muted" data-testid="plan-bank-gate-c16">
							判分侧补项（不属金标，随闸登记）：
							{d.c16.map((c, i) => (
								<span key={c.item}>
									{i ? "；" : ""}
									{c.item} {c.status}
								</span>
							))}
						</div>
					) : null}
				</CardContent>
			</Card>
			<Card>
				<CardContent className="space-y-3 py-4">
					<div className="flex flex-wrap items-center gap-3">
						<LabeledSelect
							label="级别"
							value={level}
							onChange={setLevel}
							options={[
								{ value: ALL, label: "全部" },
								...(["block", "warn", "info"] as const).map((lv) => ({
									value: lv,
									label: LEVEL_ZH[lv],
								})),
							]}
						/>
						<LabeledSelect
							label="检查项"
							value={check}
							onChange={setCheck}
							options={[
								{ value: ALL, label: "全部" },
								...uniq(all.map((r) => r.check)).map((c) => ({
									value: c,
									label: `${c} ${checkName[c] ?? ""}`,
								})),
							]}
						/>
					</div>
					{rows.length ? (
						<>
							<PlanTable
								columns={cols}
								rows={rows.slice(page * PAGE, page * PAGE + PAGE)}
								rowKey={(r) =>
									`${r.scenario_id}:${r.item ?? ""}:${r.check}:${r.level}:${r.msg ?? ""}`
								}
								density="compact"
								rowTestId="plan-bank-gate-row"
							/>
							<div className="flex items-center justify-between gap-3">
								<span className="text-xs text-text-muted">共 {rows.length} 条</span>
								<Pagination page={page} pageCount={pageCount} onChange={setPage} />
							</div>
						</>
					) : (
						<EmptyState
							title={all.length ? "没有符合条件的明细" : "这份快照逐题核下来没有问题"}
							desc={all.length ? "换个级别或检查项看看。" : "阻断、人工看、提示都是 0。"}
						/>
					)}
				</CardContent>
			</Card>
			<Drawer
				open={spec}
				onClose={() => setSpec(false)}
				width="min(820px,95vw)"
				testId="plan-bank-gate-spec"
				title="出题规范 · 能力对齐闸"
				description={d.spec.path}
			>
				<DrawerSection>
					{d.spec.text ? (
						<Markdown text={d.spec.text} />
					) : (
						<div className="text-sm text-text-muted">规范文档不在这份部署里</div>
					)}
				</DrawerSection>
			</Drawer>
		</div>
	);
}

type PlanBankSimPersona = { name: string; weight?: number | null; desc: string };
